import Head from 'next/head'
import NextLink from 'next/link'
import { Icon } from 'components/Icon'
import { pageHref } from 'features/Blog/utils/pageHref'
import { useTranslation } from 'hooks/useTranslation'
import {
  BlogPaginationRoot,
  Ellipsis,
  NavigationLink,
  PageLink,
  PageLinksList,
} from './BlogPagination.styles'
import { getPaginationRange } from './getPaginationRange'

type BlogPaginationProps = {
  page: number
  totalPages: number
  tags?: string[]
}

export const BlogPagination = ({ page, totalPages, tags = [] }: BlogPaginationProps) => {
  const { t } = useTranslation()

  if (totalPages <= 1) {
    return null
  }

  const hasPrev = page > 1
  const hasNext = page < totalPages
  const paginationRange = getPaginationRange(page, totalPages)

  return (
    <BlogPaginationRoot aria-label={t('blog:pagination.label')}>
      <Head>
        {hasPrev && <link rel="prev" href={pageHref(page - 1, tags)} />}
        {hasNext && <link rel="next" href={pageHref(page + 1, tags)} />}
      </Head>

      {hasPrev ? (
        <NextLink href={pageHref(page - 1, tags)} passHref>
          <NavigationLink aria-label={t('blog:pagination.previous-page')}>
            <Icon name="angle-left" />
          </NavigationLink>
        </NextLink>
      ) : (
        <NavigationLink
          as="span"
          aria-label={t('blog:pagination.previous-page')}
          isDisabled
          aria-disabled
        >
          <Icon name="angle-left" />
        </NavigationLink>
      )}

      <PageLinksList>
        {paginationRange.map((item, index) =>
          item === 'ellipsis' ? (
            <li key={`ellipsis-${index}`}>
              <Ellipsis aria-hidden>&hellip;</Ellipsis>
            </li>
          ) : (
            <li key={item}>
              <NextLink href={pageHref(item, tags)} passHref>
                <PageLink isActive={item === page} aria-current={item === page ? 'page' : undefined}>
                  {item}
                </PageLink>
              </NextLink>
            </li>
          )
        )}
      </PageLinksList>

      {hasNext ? (
        <NextLink href={pageHref(page + 1, tags)} passHref>
          <NavigationLink aria-label={t('blog:pagination.next-page')}>
            <Icon name="angle-right" />
          </NavigationLink>
        </NextLink>
      ) : (
        <NavigationLink
          as="span"
          aria-label={t('blog:pagination.next-page')}
          isDisabled
          aria-disabled
        >
          <Icon name="angle-right" />
        </NavigationLink>
      )}
    </BlogPaginationRoot>
  )
}

BlogPagination.displayName = 'BlogPagination'
